import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  X,
  Building2,
  FileText,
  User,
  Lock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuthStore } from '@store/authStore';
import { usePlanFeatures } from '@api/hooks/useSubscription';
import { useVendorPortalAccess } from '@api/hooks/useVendorPortal';
import { PLATFORM_NAV_CATALOG } from '@/lib/navCatalog';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  lockedFeature?: string;
}

export const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const location = useLocation();
  const currentUser = useAuthStore(state => state.currentUser);
  const hasPermission = useAuthStore(state => state.hasPermission);
  const planFeatures = usePlanFeatures();
  const { data: vendorPortalAccess } = useVendorPortalAccess();

  const isVendorTier = planFeatures.vendor_profile === true;
  const hasVendorCustomerLinks = vendorPortalAccess?.has_vendor_access === true;

  const vendorNavItems: NavItem[] = [
    { label: 'My Profile', path: '/vendor-profile', icon: User },
    { label: 'EZ AP Customers', path: '/vendor-customers', icon: Building2 },
    { label: 'Customer Invoices', path: '/vendor-invoices', icon: FileText },
  ];

  const visiblePlatformCatalog = PLATFORM_NAV_CATALOG.filter(entry => {
    const featureOK = !entry.feature || planFeatures[entry.feature] === true || planFeatures['_all_features'] === true;
    const permOK = !entry.permission || hasPermission(entry.permission);
    // Show if both gates pass, OR show locked when upsellWhenLocked=true and permission gate passes.
    return (featureOK && permOK) || (entry.upsellWhenLocked === true && permOK);
  });

  const platformNavItems: NavItem[] = visiblePlatformCatalog.map(entry => ({
    label: entry.label,
    path: entry.path,
    icon: entry.icon,
    lockedFeature: entry.feature && !(planFeatures[entry.feature] === true || planFeatures['_all_features'] === true)
      ? entry.feature
      : undefined,
  }));

  const navItems = isVendorTier
    ? vendorNavItems
    : [
        ...platformNavItems,
        ...(hasVendorCustomerLinks
          ? [
              { label: 'Vendor Profile', path: '/vendor-profile', icon: User },
              { label: 'EZ AP Customers', path: '/vendor-customers', icon: Building2 },
              { label: 'Customer Invoices', path: '/vendor-invoices', icon: FileText },
            ]
          : []),
      ];

  const isActive = (path: string) => {
    const [pathname, search] = path.split('?');
    if (location.pathname !== pathname) return false;
    return search ? location.search === `?${search}` : true;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm lg:hidden z-30"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'fixed left-0 top-0 h-screen w-64 z-40',
          'bg-surface-base border-r border-border-default',
          'flex flex-col',
          'transform transition-transform duration-200',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-border-subtle">
          <span className="text-sm font-semibold text-content-muted uppercase tracking-wider">
            Navigation
          </span>
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 hover:bg-surface-raised rounded-lg transition-colors text-content-muted hover:text-content-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {navItems.map(({ label, path, icon: Icon, lockedFeature }) => {
            const isLocked = lockedFeature ? !planFeatures[lockedFeature] : false;

            return (
              <Link
                key={path}
                to={path}
                onClick={onClose}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive(path)
                    ? 'bg-primary-100 text-primary-600'
                    : 'text-content-secondary hover:bg-surface-raised hover:text-content-primary'
                )}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1">{label}</span>
                {isLocked && <Lock className="h-3.5 w-3.5 text-content-muted" />}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-border-subtle">
          {currentUser && (
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                {(currentUser.first_name?.[0] ?? '') + (currentUser.last_name?.[0] ?? '')}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-content-primary truncate">
                  {currentUser.first_name} {currentUser.last_name}
                </p>
                <p className="text-xs text-content-muted truncate">{currentUser.email}</p>
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
